<template>
  <div>
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <add-cheque
      v-if="showChequeForm"
      @modal-closed="showChequeForm = false"
      @cheque-added="addChequeHandler"
    />

    <b-row>
      <b-col align="right">
        <b-button
          variant="outline-info"
          class="mb-1 ml-2"
          :disabled="loading || selectedCheques.length <= 0"
          @click="openBulkModal('courier')"
        >
          Change Courier
        </b-button>

        <b-button
          variant="outline-warning"
          class="mb-1 ml-2"
          :disabled="loading || selectedCheques.length <= 0"
          @click="openBulkModal('supplier')"
        >
          Change Supplier
        </b-button>

        <b-button
          variant="outline-primary"
          class="mb-1 ml-2"
          @click="showChequeForm = true"
        >
          Add Issued Cheque
        </b-button>
      </b-col>
    </b-row>

    <b-table
      v-if="!loading && !loadingError"
      :items="results"
      :fields="tableFields"
      responsive="sm"
      striped
      hover
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      class="compact-table"
      no-local-sorting
      @sort-changed="sortingChanged"
    >
      <template #head(select)>
        <b-form-checkbox
          v-model="isAllChequeSelected"
          :disabled="results.length === 0"
          @change="handleAllChequeSelectChange"
        />
      </template>

      <template #cell(select)="value">
        <b-form-checkbox
          v-model="selectedCheques"
          :value="value.item.id"
        />
      </template>

      <template #cell(issue_date)="data">
        {{ data.item.issue_date && formatedDate(data.item.issue_date) }}
      </template>
      <template #cell(deposited_on)="data">
        {{ data.item.deposited_on && formatedDate(data.item.deposited_on) }}
      </template>
      <template #cell(amount)="data">
        {{ formateNumber(data.item.amount) }}
      </template>
      <template #cell(status)="{item}">
        <app-chip :variant="getStausChipVariant(item.status)">
          {{ item.status.charAt(0).toUpperCase() + item.status.slice(1) }}
        </app-chip>
      </template>
      <template #cell(action)="data">
        <feather-icon
          v-b-tooltip.hover="{boundary:'window'}"
          title="Update Status"
          icon="EditIcon"
          size="20"
          class="cursor-pointer mr-1"
          @click="selectedUpdateChequeId = data.item.id"
        />
        <feather-icon
          v-b-tooltip.hover="{boundary:'window'}"
          title="View Details"
          icon="EyeIcon"
          size="20"
          class="cursor-pointer mr-1"
          @click="selectedCheckDetailId = data.item.id"
        />
      </template>
    </b-table>

    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => {currentPage = page}"
    />

    <Cheque-update-modal
      v-if="selectedUpdateChequeId"
      :id="selectedUpdateChequeId"
      @modal-closed="selectedUpdateChequeId = null"
      @updated="fetchItems"
    />
    <Cheque-details-modal
      v-if="selectedCheckDetailId"
      :id="selectedCheckDetailId"
      @modal-closed="selectedCheckDetailId = null"
    />

    <cheque-bulk-update-modal
      v-if="bulkModalIdentifier"
      :key="bulkModalIdentifier"
      :selected-ids="selectedCheques"
      :identifier="bulkModalIdentifier"
      @modal-closed="bulkModalIdentifier = null"
      @updated="onBulkUpdated"
    />
  </div>
</template>

<script>
import {
  BSpinner, BTable, BAlert, BRow, BCol, BButton, VBTooltip,
  BFormCheckbox,
} from 'bootstrap-vue'
import axios from 'axios'

import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'
import AppChip from '@/components/UI/AppChip.vue'
import AddCheque from './AddCheque.vue'
import DetailedPagination from '../UI/DetailedPagination.vue'
import ChequeUpdateModal from './ChequeUpdateModal.vue'
import ChequeDetailsModal from './ChequeDetailsModal.vue'
import ChequeBulkUpdateModal from './ChequeBulkUpdateModal.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BTable,
    BRow,
    BCol,
    BButton,
    BAlert,
    BFormCheckbox,
    AppChip,
    DetailedPagination,
    AddCheque,
    ChequeUpdateModal,
    ChequeDetailsModal,
    ChequeBulkUpdateModal,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,

      results: [],
      tableFields: [
        { key: 'select' },
        { key: 'formatted_cheque_number', label: 'Cheque Number', sortable: true },
        { key: 'supplier_identifier', label: 'Supplier', sortable: true },
        { key: 'issue_date', label: 'Issue Date', sortable: true },
        { key: 'deposited_on', label: 'Deposited On', sortable: true },
        {
          key: 'amount', label: 'Amount', sortable: true, class: 'text-right pr-2',
        },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'action', label: 'Action', sortable: true },
      ],

      // Row selection
      selectedCheques: [],
      isAllChequeSelected: false,

      // Bulk update modal identifier: null | 'supplier' | 'courier'
      bulkModalIdentifier: null,

      selectedUpdateChequeId: null,
      selectedCheckDetailId: false,
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      sortBy: 'generation_date',
      sortDesc: true,
      submitting: false,
      showChequeForm: false,
    }
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchItems()
      }
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },

    fetchItems() {
      const params = {
        sort_by: this.sortBy,
        sort_desc: this.sortDesc,
        page: this.currentPage,
        per_page: this.perPage,
        ...this.filters,
      }
      if (this.filters?.cheque_number_min) {
        params.cheque_number_min = this.filters?.cheque_number_min
      }
      if (this.filters?.cheque_number_max) {
        params.cheque_number_max = this.filters?.cheque_number_max
      }
      if (this.filters?.min_amount) {
        params.min_amount = this.filters?.min_amount
      }
      if (this.filters?.max_amount) {
        params.max_amount = this.filters?.max_amount
      }
      if (this.filters?.status) {
        params.status = this.filters?.status
      }
      if (this.filters?.sent_through) {
        params.sent_through = this.filters?.sent_through
      }
      this.loading = true
      axios.get(`/accounts/vatika_cheques/?${getParsedQueryString(params)}`)
        .then(res => {
          this.results = res.data.results
          this.totalItems = res.data.count
          this.loadingError = null
          this.loading = false
          this.selectedCheques = []
          this.isAllChequeSelected = false
        }).catch(error => {
          this.loadingError = error?.response?.data?.error || 'Something went wrong'
          this.loading = false
        })
    },

    handleAllChequeSelectChange(isChecked) {
      this.selectedCheques = isChecked
        ? this.results.map(data => data.id)
        : []
    },

    openBulkModal(identifier) {
      this.bulkModalIdentifier = identifier
    },

    onBulkUpdated() {
      this.bulkModalIdentifier = null
      this.selectedCheques = []
      this.isAllChequeSelected = false
      this.fetchItems()
    },

    addChequeHandler() {
      this.showChequeForm = false
      this.currentPage = 1
      this.fetchItems()
    },

    getStausChipVariant(value) {
      if (value === 'cancelled') return 'danger'
      if (value === 'bounced') return 'warning'
      if (value === 'deposited') return 'success'
      if (value === 'security') return 'primary'
      return 'secondary'
    },
  },
}
</script>
